import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import ReactSelect from "../../../Components/PortalSelect";

// react-select styled to follow the theme tokens (light + dark)
const colourStyles = {
  control: (styles, state) => ({
    ...styles,
    minHeight: 42,
    backgroundColor: "var(--color-surface)",
    borderColor: state.isFocused ? "var(--color-primary)" : "var(--color-border)",
    borderRadius: 10,
    boxShadow: state.isFocused ? "0 0 0 3px var(--color-primary-weak)" : "none",
    "&:hover": { borderColor: "var(--color-primary)" },
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    zIndex: 30,
  }),
  singleValue: (styles) => ({ ...styles, color: "var(--color-text-primary)" }),
  input: (styles) => ({ ...styles, color: "var(--color-text-primary)" }),
  placeholder: (styles) => ({ ...styles, color: "var(--color-text-tertiary)" }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isFocused
      ? "var(--color-hover)"
      : "var(--color-surface)",
    color: "var(--color-text-primary)",
    ":active": { backgroundColor: "var(--color-hover)" },
  }),
};

// Themed text field bound to formik (module-scope so inputs keep focus)
const Field = ({ formik, label, name, mono, full, placeholder }) => (
  <div className={`ef-field ${full ? "ef-field--full" : ""}`}>
    <label className="ef-label" htmlFor={`ef-${name}`}>
      {label}
    </label>
    <input
      id={`ef-${name}`}
      className={`ef-input ${mono ? "ef-mono" : ""}`}
      type="text"
      placeholder={placeholder || `Enter ${label}`}
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
    />
  </div>
);

const SupplierEdit = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const item = location?.state?.item;

  const [initialValues, setInitialValues] = useState({
    name: "",
    email: "",
    mobile_number: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    status: "",
    role: "",
    company_name: "",
    business_type: "",
    gst_number: "",
    website: "",
    company_logo: "",
    machine_configuration: "",
    supplier_type: "",
    account_holder_name: "",
    bank_name: "",
    branch_name: "",
    account_number: "",
    ifsc_code: "",
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        // ✅ If file is selected, you may need a separate upload endpoint
        // If not needed, we skip the file completely
        const payload = { ...values };
        delete payload.company_logo; // optional: if backend doesn’t accept file field in JSON
        const endpoint =
          item?.user_type == "supplier" || item?.user_type == "offline_supplier"
            ? "/admin/offline_supplier/"
            : "/admin/offline_buyer/";
        const res = await axios.put(
          `${process.env.REACT_APP_URL}${endpoint}${item?.id}`,
          payload, // ✅ send pure JSON
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
              "Content-Type": "application/json", // ✅ raw JSON type
            },
          }
        );

        if (res.status === 200) {
          toast.success(res.data.message || "Supplier updated successfully");
          // This is the generic "edit any user" page (buyers, subadmins,
          // etc., not just suppliers) — it always redirected to the supplier
          // list regardless of who was actually being edited. Go back to
          // wherever the edit was opened from instead, same as Cancel/Back.
          Navigate(-1);
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to update supplier.");
      }
    },
  });

  useEffect(() => {
    if (item) {
      setInitialValues({
        name: item?.name || "",
        email: item?.email || "",
        mobile_number: item?.mobile_number || "",
        address: item?.address || "",
        city: item?.city || "",
        state: item?.state || "",
        country: item?.country || "",
        pincode: item?.pincode || "",
        status: item?.status || "",
        role: item?.role || "",
        company_name: item?.company_name || "",
        business_type: item?.business_type || "",
        gst_number: item?.gst_number || "",
        website: item?.website || "",
        company_logo: item?.company_logo || "",
        machine_configuration: item?.machine_configuration || "",
        supplier_type: item?.supplier_type?.toLowerCase() || "",
        account_holder_name: item?.account_holder_name || "",
        bank_name: item?.bank_name || "",
        branch_name: item?.branch_name || "",
        account_number: item?.account_number || "",
        ifsc_code: item?.ifsc_code || "",
      });
    }
  }, [item]);

  const isSupplier =
    item?.user_type === "supplier" || item?.user_type === "offline_supplier";

  return (
    <div className="ef">
      <Toaster position="top-center" reverseOrder={false} />

      <form onSubmit={formik.handleSubmit}>
        {/* page header */}
        <div className="ef-head">
          <div>
            <h1 className="ef-title">Edit User</h1>
            <p className="ef-sub">
              Update profile and business details
              {item?.name ? (
                <>
                  {" "}
                  for <strong>{item.name}</strong>
                </>
              ) : null}
              .
            </p>
          </div>
          {formik.dirty && (
            <div className="ef-unsaved">
              <span className="ef-unsaved__dot" />
              Unsaved changes
            </div>
          )}
        </div>

        {/* Basic Information */}
        <div className="ef-card">
          <div className="ef-card__head">
            <span className="ef-card__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </span>
            <div>
              <h3 className="ef-card__title">Basic Information</h3>
              <p className="ef-card__sub">Identity, contact and address details.</p>
            </div>
          </div>
          <div className="ef-card__body ef-grid">
            <Field formik={formik} label="Name" name="name" />
            <Field formik={formik} label="Email" name="email" />
            <Field formik={formik} label="Phone" name="mobile_number" mono />
            <div className="ef-field">
              <label className="ef-label">Role</label>
              <div className="ef-input ef-input--readonly">
                {(item?.user_type || formik.values.role || "—").replaceAll(
                  "_",
                  " "
                )}
              </div>
            </div>
            <Field formik={formik} label="Address" name="address" full />
            <Field formik={formik} label="City" name="city" />
            <Field formik={formik} label="State" name="state" />
            <Field formik={formik} label="Country" name="country" />
            <Field formik={formik} label="PinCode" name="pincode" mono />
          </div>
        </div>

        {/* Company Details */}
        <div className="ef-card">
          <div className="ef-card__head">
            <span className="ef-card__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            </span>
            <div>
              <h3 className="ef-card__title">Company Details</h3>
              <p className="ef-card__sub">Business profile and compliance.</p>
            </div>
          </div>
          <div className="ef-card__body ef-grid">
            <Field formik={formik} label="Company Name" name="company_name" />
            <Field formik={formik} label="Business Type" name="business_type" />
            <Field formik={formik} label="Website" name="website" />
            <Field formik={formik} label="GST Number" name="gst_number" mono />

            {isSupplier && (
              <>
                <Field
                  formik={formik}
                  label="Machine Configuration"
                  name="machine_configuration"
                  full
                />
                <div className="ef-field">
                  <label className="ef-label">Supplier Type</label>
                  <ReactSelect
                    options={[
                      { label: "Manufacturer", value: "manufacturer" },
                      { label: "Trader", value: "trader" },
                      { label: "Retailer", value: "retailer" },
                    ]}
                    isClearable={false}
                    styles={colourStyles}
                    placeholder="Select Supplier Type"
                    name="supplier_type"
                    onChange={(e) =>
                      formik.setFieldValue("supplier_type", e?.value)
                    }
                    value={
                      [
                        { label: "Manufacturer", value: "manufacturer" },
                        { label: "Trader", value: "trader" },
                        { label: "Retailer", value: "retailer" },
                      ].find(
                        (opt) => opt.value === formik.values.supplier_type
                      ) || null
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bank Details */}
        <div className="ef-card">
          <div className="ef-card__head">
            <span className="ef-card__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="22" x2="21" y2="22" />
                <line x1="6" y1="18" x2="6" y2="11" />
                <line x1="10" y1="18" x2="10" y2="11" />
                <line x1="14" y1="18" x2="14" y2="11" />
                <line x1="18" y1="18" x2="18" y2="11" />
                <polygon points="12 2 20 7 4 7" />
              </svg>
            </span>
            <div>
              <h3 className="ef-card__title">Bank Details</h3>
              <p className="ef-card__sub">Payout and settlement information.</p>
            </div>
          </div>
          <div className="ef-card__body ef-grid">
            <Field formik={formik} label="Account Holder Name" name="account_holder_name" />
            <Field formik={formik} label="Bank Name" name="bank_name" />
            <Field formik={formik} label="Branch Name" name="branch_name" />
            <Field formik={formik} label="Account Number" name="account_number" mono />
            <Field formik={formik} label="IFSC Code" name="ifsc_code" mono />
          </div>
        </div>

        {/* sticky action bar */}
        <div className="ef-actions">
          <div className="ef-actions__hint">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            Changes are saved to the user's profile immediately.
          </div>
          <div className="ef-actions__btns">
            <button
              type="button"
              className="ef-btn"
              onClick={() => Navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ef-btn ef-btn--primary"
              disabled={formik.isSubmitting}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              {formik.isSubmitting ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </form>

      <EditStyles />
    </div>
  );
};

function EditStyles() {
  return (
    <style>{`
.ef{ color:var(--color-text-primary); padding-bottom:80px; }
.ef *{ box-sizing:border-box; }
.ef-mono{ font-family:var(--font-mono); }

.ef-head{ display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:18px; }
.ef-title{ margin:0; font-size:24px; font-weight:800; letter-spacing:-.025em; }
.ef-sub{ margin:5px 0 0; font-size:13.5px; color:var(--color-text-secondary); }
.ef-unsaved{ display:flex; align-items:center; gap:8px; font-size:12px; font-weight:700; color:var(--color-warning-text); background:var(--color-warning-bg); border-radius:9px; padding:7px 12px; }
.ef-unsaved__dot{ width:7px; height:7px; border-radius:50%; background:var(--color-warning-text); }

.ef-card{ background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; box-shadow:var(--shadow-card); overflow:hidden; margin-bottom:16px; }
.ef-card__head{ display:flex; align-items:center; gap:11px; padding:16px 22px; border-bottom:1px solid var(--color-border-soft); }
.ef-card__icon{ width:32px; height:32px; border-radius:9px; background:var(--color-primary-weak); color:var(--color-primary); display:flex; align-items:center; justify-content:center; flex:0 0 32px; }
.ef-card__title{ margin:0; font-size:14.5px; font-weight:700; }
.ef-card__sub{ margin:2px 0 0; font-size:12px; color:var(--color-text-secondary); }
.ef-card__body{ padding:22px; }

.ef-grid{ display:grid; grid-template-columns:1fr 1fr; gap:20px 24px; }
@media (max-width:640px){ .ef-grid{ grid-template-columns:1fr; } }
.ef-field{ display:flex; flex-direction:column; }
.ef-field--full{ grid-column:1 / -1; }
.ef-label{ font-size:12px; font-weight:700; color:var(--color-text-secondary); margin-bottom:7px; }
.ef-input{ width:100%; height:42px; padding:0 13px; border:1px solid var(--color-border); border-radius:10px; background:var(--color-surface); font-family:inherit; font-size:13.5px; font-weight:600; color:var(--color-text-primary); outline:none; transition:border-color .15s ease, box-shadow .15s ease; }
.ef-input::placeholder{ color:var(--color-text-tertiary); font-weight:500; }
.ef-input:focus{ border-color:var(--color-primary); box-shadow:0 0 0 3px var(--color-primary-weak); }
.ef-input--readonly{ display:flex; align-items:center; background:var(--color-surface-2); color:var(--color-text-secondary); text-transform:capitalize; cursor:default; }
textarea.ef-input{ height:auto; min-height:64px; padding:11px 13px; resize:vertical; }

/* sticky action bar — docked rounded card aligned with the form */
.ef-actions{ position:sticky; bottom:16px; z-index:15; margin-top:4px; background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; box-shadow:var(--shadow-card-hover); padding:14px 18px; display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
.ef-actions__hint{ display:flex; align-items:center; gap:8px; font-size:12.5px; color:var(--color-text-secondary); }
.ef-actions__btns{ margin-left:auto; display:flex; align-items:center; gap:10px; }
.ef-btn{ display:flex; align-items:center; gap:8px; height:40px; padding:0 18px; border-radius:10px; border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-primary); font-family:inherit; font-size:13px; font-weight:700; cursor:pointer; transition:background .15s ease; }
.ef-btn:hover{ background:var(--color-hover); }
.ef-btn--primary{ border:0; color:#fff; background:linear-gradient(135deg,var(--color-primary),var(--color-primary-2)); box-shadow:0 2px 8px rgba(79,70,229,.3); padding:0 20px; }
.ef-btn--primary:hover{ filter:brightness(1.06); background:linear-gradient(135deg,var(--color-primary),var(--color-primary-2)); }
.ef-btn:disabled{ opacity:.65; cursor:not-allowed; }
@media (max-width:560px){ .ef-actions__hint{ display:none; } .ef-actions__btns{ margin-left:0; width:100%; } .ef-btn{ flex:1; justify-content:center; } }
    `}</style>
  );
}

export default SupplierEdit;
